// ============================================
// 날짜 resolver — aiGuide.js에서 책임 분리 (순수 이동)
// ============================================
// 날짜 토큰/템플릿 치환/기본기간 resolve 등 cross-cutting 날짜 유틸 모음.
// 무의존(순수). aiIntentionMeta/aiPromptBuilders가 본 모듈을 import.

// 오늘 날짜 시스템 메시지 생성 //
// RL-5 — ISO YYYY-MM-DD (KST). link-env <today>와 동일 포맷 → SQL/API 파라미터 직접 사용 안전.
// 결정성 + 영어 prompt 일관성. 이전: 한국어 "2026년 5월 18일".
export function getTodaySystemMessage() {
  // en-CA locale은 YYYY-MM-DD 포맷 강제 + Asia/Seoul timezone 명시 (UTC 차이 1일 회피)
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
  return `Today: ${today}`;
}

// 날짜 토큰 단일 진실원 (resolver map)
// LLM prompt 예시와 코드 측 SQL 치환이 동일한 resolver 사용 → drift 0
const _pad = (n) => String(n).padStart(2, '0');
const _fmtDate = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;

export const DATE_TOKEN_RESOLVERS = {
  today: (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return _fmtDate(d);
  },
  year: (offset = 0) => String(new Date().getFullYear() + offset),
};

// "{token[+-]N}" 패턴 → resolver 호출. 알 수 없는 token은 원문 보존
export function substituteDateTemplates(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)([+-]\d+)?\}/g, (match, token, off) => {
    const resolver = DATE_TOKEN_RESOLVERS[token];
    if (!resolver) return match;
    const offset = off ? parseInt(off, 10) : 0;
    return resolver(offset);
  });
}

// ============================================
// defaultPeriod resolver — intention.defaultPeriod 토큰 → 실제 YYYY-MM-DD
// ============================================
// 토큰: 'today' | 'year_start' | 'this_week_start' | 'this_week_end' | '[+-]Nd'
// 사용처: call_api 호출 시 startDate/endDate 미지정 case에서 frontend가 자동 채움

function _formatYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function _addDays(d, days) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
}

/**
 * @param {string} token — 'today' | 'year-start'|'year_start' | 'year-end' | 'month-start' | 'month-end' | 'this_week_start' | 'this_week_end' | '[+-]Nd'
 * @param {Date} [today]
 * @returns {string|null} YYYY-MM-DD or null if 토큰 인식 실패
 */
export function resolveDateToken(token, today = new Date()) {
  if (!token || typeof token !== 'string') return null;
  if (token === 'today') return _formatYMD(today);
  // year-start (hyphen) + year_start (underscore, backward compat)
  if (token === 'year-start' || token === 'year_start') return `${today.getFullYear()}-01-01`;
  if (token === 'year-end' || token === 'year_end') return `${today.getFullYear()}-12-31`;
  if (token === 'month-start' || token === 'month_start') {
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${today.getFullYear()}-${m}-01`;
  }
  if (token === 'month-end' || token === 'month_end') {
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${today.getFullYear()}-${m}-${String(lastDay).padStart(2, '0')}`;
  }
  if (token === 'this_week_start' || token === 'this_week_end') {
    const day = today.getDay();  // 0=Sun, 1=Mon, ... 6=Sat
    // 월요일 시작 (1=Mon)
    const offsetToMonday = day === 0 ? -6 : 1 - day;
    if (token === 'this_week_start') return _formatYMD(_addDays(today, offsetToMonday));
    return _formatYMD(_addDays(today, offsetToMonday + 6));  // 일요일
  }
  const m = /^([+-]?\d+)d$/.exec(token);
  if (m) return _formatYMD(_addDays(today, parseInt(m[1], 10)));
  return null;
}

/**
 * @param {object} intention — LOCAL_INTENTIONS_DATA.intentions[*] 원본
 * @param {Date} [today]
 * @returns {{startDate:string, endDate:string}|null}
 */
export function resolveDefaultPeriod(intention, today = new Date()) {
  if (!intention?.defaultPeriod) return null;
  const startDate = resolveDateToken(intention.defaultPeriod.start, today);
  const endDate = resolveDateToken(intention.defaultPeriod.end, today);
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
}

// USER_TIME_KW_RE: 사용자가 explicit time keyword 입력 시 LLM dates 보존 신호 (한/영 bilingual)
//   - 절대 시점: 오늘/어제/내일, 지난주/이번주/다음주/금주, 지난달/이번달/다음달/이달/금월, 작년/올해/내년/이번년, 당월/당주/당일 (지난/이번/다음+주/달/년은 띄어쓰기 허용)
//   - 상대 기간: 최근 N일/N주/N달, 지난 N일/N주/N달, N일/N주/N달 전, 며칠/몇 주/몇 달 전
//   - 명시 날짜: N월 (1-12), N년, YYYY-MM-DD, N주차, 분기(단독 토큰 — 지난/이번/전/N분기/N사분기/분기별 포괄, DEMO-37 KO 잔존 fix), 상반기/하반기, 연초/연말/월초/월말/주초/주말
//   - 기간 표현: 사이/부터/까지/동안 + 시간 단어
//   - 영어 (DEMO-25-RENEWAL-EN fix — EN 시간표현 미인식 시 defaultPeriod가 LLM의 정확한 날짜를 덮어쓰는 문제):
//     today/yesterday/tomorrow, past|last|this|next|previous|recent|coming + (N) year/month/week/day/quarter/half,
//     N|a|few|couple|several + 단위 + ago, year-to-date/ytd, q1~q4, 월 이름(전체/축약)
//     주의: \bmay\b는 조동사 may와 충돌 가능(허용 — false positive 시 LLM dates 보존으로 폴백, 기존 한국어 '사이/동안'급 관용 범위)
export const USER_TIME_KW_RE = /오늘|어제|내일|지난\s*주|이번\s*주|다음\s*주|금주|지난\s*달|이번\s*달|다음\s*달|이달|금월|당월|당주|당일|작년|올해|내년|이번\s*년|이번\s*해|올\s*해|지난\s*해|이전\s*해|최근\s*\d+|지난\s*\d+|\d+\s*일\s*전|\d+\s*주\s*전|\d+\s*달\s*전|며칠\s*전|몇\s*주\s*전|몇\s*달\s*전|\d{1,2}\s*월|\d{4}\s*년|\d{4}-\d{2}-\d{2}|\d+\s*주차|분기|상반기|하반기|연초|연말|월초|월말|주초|주말|사이|부터|까지|동안|today|yesterday|tomorrow|(?:past|last|this|next|previous|recent|coming)\s+(?:\d+\s+)?(?:years?|months?|weeks?|days?|quarters?|half)|(?:\d+|a|few|couple(?:\s+of)?|several)\s+(?:years?|months?|weeks?|days?)\s+ago|year[\s-]?to[\s-]?date|\bytd\b|\bq[1-4]\b|january|february|march|april|\bmay\b|june|july|august|september|october|november|december|\bjan\b|\bfeb\b|\bmar\b|\bapr\b|\bjun\b|\bjul\b|\baug\b|\bsept?\b|\boct\b|\bnov\b|\bdec\b/i;

// [QUARTER OVERRIDE] 분기 표현 결정적 날짜 해석 — 분기 표현은 의미가 유일(직전/현재 달력 분기)하므로 //
// LLM의 stochastic 오계산(예: "지난 분기"를 현재 분기로 산출 — DEMO-37 run7)과 무관하게 코드가 교정. //
// 매칭 우선순위: 작년 N분기 > 지난/저번/직전/전 분기 > 이번/올/현재/금 분기 > N분기(올해) > qN. 미매칭 시 null (개입 없음). //
// 단일 소스 — AgentSession/nodeAgent 공용 (call_api 직전 date params 강제 교정). //
export function resolveQuarterOverride(userInput, today = new Date()) {
  if (!userInput || typeof userInput !== 'string') return null;

  const y = today.getFullYear();
  const curQ = Math.floor(today.getMonth() / 3);  // 0-based

  let targetYear = y;
  let targetQ = null;  // 0-based

  const lastYearN = userInput.match(/(?:작년|지난\s*해)\s*([1-4])\s*(?:사)?분기/);
  const explicitN = userInput.match(/(?:^|[^가-힣\d])([1-4])\s*(?:사)?분기/);
  const qn = userInput.match(/\bq([1-4])\b/i);

  if (lastYearN) {
    targetYear = y - 1;
    targetQ = parseInt(lastYearN[1], 10) - 1;
  }
  else if (/(?:지난|저번|직전|전)\s*분기|(?:last|previous)\s+quarter/i.test(userInput)) {
    targetQ = curQ - 1;
    if (targetQ < 0) {
      targetQ = 3;
      targetYear = y - 1;
    }
  }
  else if (/(?:이번|올|현재|금)\s*분기|this\s+quarter/i.test(userInput)) {
    targetQ = curQ;
  }
  else if (explicitN) {
    targetQ = parseInt(explicitN[1], 10) - 1;
  }
  else if (qn) {
    targetQ = parseInt(qn[1], 10) - 1;
  }

  if (targetQ == null) return null;

  const startMonth = targetQ * 3;  // 0-based
  const start = new Date(targetYear, startMonth, 1);
  const end = new Date(targetYear, startMonth + 3, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return { startDate: fmt(start), endDate: fmt(end) };
}

// [DAY OVERRIDE] 단일 절대일("오늘/어제/내일/그저께/모레")은 의미가 유일(today±N 고정) — 분기처럼 LLM의 //
//   stochastic 오산출과 무관하게 코드가 결정적 교정. (REPLAY T10: "어제"를 startDate=2026-06-14로 명시, //
//   정답 06-15. 같은 turn의 sql_gen 경로는 06-15 정답 — 동일 Today 컨텍스트에서 경로/run별 비결정 확인). //
//   resolveQuarterOverride 동형 단일 소스. 미매칭 시 null(개입 없음). //
//   매칭 우선순위: 그저께/그제(-2) > 어제(-1) > 모레(+2) > 내일(+1) > 오늘(0). //
//   false positive 차단: 비교어(보다/대비/vs/than)·범위어(부터/까지/사이/동안)·다른 명시기간 //
//   (N월/YYYY-MM-DD/지난·이번·다음 주·달/분기/상하반기/작년/올해/최근) 동반 시 null — 단일일 강제 부적절. //
export function resolveDayOverride(userInput, today = new Date()) {
  if (!userInput || typeof userInput !== 'string') return null;
  // 비교/범위 맥락 — 단일일 강제 부적절
  if (/보다|대비|비교|\bvs\b|than|부터|까지|사이|동안/i.test(userInput)) return null;
  // 단일 절대일 외 다른 명시 기간 동반 시 충돌 회피
  if (/\d{1,2}\s*월|\d{4}-\d{2}-\d{2}|지난\s*[주달]|이번\s*[주달]|다음\s*[주달]|분기|상반기|하반기|작년|올해|최근/i.test(userInput)) return null;

  let offset = null;
  if (/그저께|그제|day\s+before\s+yesterday/i.test(userInput)) offset = -2;
  else if (/어제|yesterday/i.test(userInput)) offset = -1;
  else if (/모레|day\s+after\s+tomorrow/i.test(userInput)) offset = 2;
  else if (/내일|tomorrow/i.test(userInput)) offset = 1;
  else if (/오늘|today/i.test(userInput)) offset = 0;
  if (offset === null) return null;

  const d = new Date(today);
  d.setDate(d.getDate() + offset);
  const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  const day = fmt(d);
  return { startDate: day, endDate: day };
}

// [RELATIVE MONTH RANGE] "지난/최근 N개월", "지난달/저번달/전달" → 결정적 date range. //
//   resolveQuarterOverride/resolveDayOverride 동형 단일 소스 (닫힌 날짜 어휘 — 키워드 게이트 부채 아님). 미매칭 시 null(개입 없음). //
//   "지난 N개월" = [today-(N-1)월 1일 ~ today] rolling window(당월 포함). "지난달/저번달/전달" = 직전 달력월 전체. //
//   "이번 달/이번 주"는 미매칭 — 현재월 default 유지(호출부 fallback). 명시월(YYYY월)/명시범위 우선은 호출부 precedence에서 처리. //
export function resolveRelativeMonthRange(userInput, today = new Date()) {
  if (!userInput || typeof userInput !== 'string') return null;
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // 지난/최근 N개월 (N개월간/N개월 동안 포함). N>=2; N=1은 아래 지난달 처리로 위임.
  const nMonth = userInput.match(/(?:지난|최근|근)\s*(\d+)\s*개월|(\d+)\s*개월\s*(?:간|동안)/);
  const n = nMonth ? parseInt(nMonth[1] || nMonth[2], 10) : null;
  if (n && n >= 2) {
    const start = new Date(today.getFullYear(), today.getMonth() - (n - 1), 1);
    return { startDate: fmt(start), endDate: fmt(today), label: `최근 ${n}개월` };
  }

  // 지난달/저번달/전달/지난 1개월 = 직전 달력월 전체
  if (/지난\s*달|저번\s*달|전달|지난\s*1\s*개월/.test(userInput)) {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate: fmt(start), endDate: fmt(end), label: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}월` };
  }

  return null;
}

// ABS_PERIOD_KW_RE: 절대(달력) 기간 표현 — LLM이 startDate/endDate를 누락하면 백엔드 전체기간/-30d로는
// 대체 불가한 표현들 (분기/상하반기/작년/올해/N월/지난달 등). DATE PARAM GUARD에서 사용:
// 절대 기간 표현 + date params 모두 누락 → call_api 차단 + 날짜 명시 재호출 유도 (DEMO-37: 무일자 호출이
// 백엔드 전체기간(2024~) 응답을 받아 "지난 분기" 질문에 2년 누적 매출로 답한 사고 차단).
// "최근/지난 N일" 같은 상대 표현은 defaultPeriod(-30d)가 정당하므로 제외. 단일 소스 — AgentSession/nodeAgent 공용.
export const ABS_PERIOD_KW_RE = /분기|사분기|상반기|하반기|작년|올해|재작년|지난\s*[주달]|이번\s*[주달]|이달|금월|당월|지난\s*해|이번\s*해|올\s*해|\d{1,2}\s*월|\d{4}\s*년|\d{4}-\d{2}-\d{2}|(?:last|this|previous)\s+(?:quarter|year|month|week)|\bq[1-4]\b|year[\s-]?to[\s-]?date|\bytd\b|january|february|march|april|\bmay\b|june|july|august|september|october|november|december/i;

// [STALE PERIOD RELABEL] 상대기간 후속 턴 거짓 라벨 — detector + resolver (운영 5차 rid 63/65/68) //
//   본질: 후속 턴이 새 절대기간("2026년 기준"/"이번달"/"올해")을 요청했는데 LLM이 신규 call_api 미트리거 →
//         직전 다른 기간 버퍼(dateRange)를 재사용 + finalText에서 새 기간으로 거짓 라벨(다음 턴 AI 자백 = 자기모순).
//   기존 NARROW_PERIOD_HONEST_INFORM 가드는 "만" 입자 필수 → "기준/이번달"(만 없음) 미매칭 gap.
//   해소: "만" 부재 + 절대기간 키워드 resolve결과 월 set이 버퍼 dateRange 월 set과 다름 + 본 turn call_api 미호출
//         + 버퍼 items + dateRange 보유(= period intention) → show_display/finish 차단 + 새 기간 call_api 재호출 nudge.
//   "만" narrow는 기존 가드(honest-inform/compare)에 위임 — 두 가드 family 분리(충돌·L21 회귀 차단). 단일 소스(AgentSession/nodeAgent/finishPolicy 공용).

// 절대기간 표현 → {startDate,endDate} resolve (비교용). 미인식/range 모호 시 null(개입 없음). //
//   precedence: quarter > 명시 N년/올해/작년 > 상하반기 > 이번달 > 지난달·지난 N개월 > 단일 N월(현재년). //
export function resolveRequestedAbsolutePeriod(userInput, today = new Date()) {
  if (!userInput || typeof userInput !== 'string') return null;
  const y = today.getFullYear();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // 1) 분기 (지난/이번/N분기) — 의미 유일
  const q = resolveQuarterOverride(userInput, today);
  if (q) return { startDate: q.startDate, endDate: q.endDate };

  // 2) 명시 4자리 연도 "2026년"
  const ym = userInput.match(/(\d{4})\s*년/);
  if (ym) {
    const yr = parseInt(ym[1], 10);
    return { startDate: `${yr}-01-01`, endDate: `${yr}-12-31` };
  }
  // 올해/금년/이번 해 (standalone)
  if (/올해|금년|올\s*해|이번\s*해|이번\s*년/.test(userInput)) {
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }
  // 작년/재작년/지난 해
  if (/재작년/.test(userInput)) return { startDate: `${y - 2}-01-01`, endDate: `${y - 2}-12-31` };
  if (/작년|지난\s*해|전년/.test(userInput)) return { startDate: `${y - 1}-01-01`, endDate: `${y - 1}-12-31` };

  // 3) 상반기/하반기
  if (/상반기/.test(userInput)) return { startDate: `${y}-01-01`, endDate: `${y}-06-30` };
  if (/하반기/.test(userInput)) return { startDate: `${y}-07-01`, endDate: `${y}-12-31` };

  // 4) 이번 달/이달/금월/당월
  if (/이번\s*달|이달|금월|당월/.test(userInput)) {
    const start = new Date(y, today.getMonth(), 1);
    const end = new Date(y, today.getMonth() + 1, 0);
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  // 5) 지난달/저번달/전달/지난 N개월/최근 N개월
  const rel = resolveRelativeMonthRange(userInput, today);
  if (rel) return { startDate: rel.startDate, endDate: rel.endDate };

  // 6) 단일 "N월"(현재년) — range 마커(부터/까지/사이/동안/~) 동반 시 미개입(LLM이 명시 범위 호출)
  if (!/부터|까지|사이|동안|~/.test(userInput)) {
    const mm = userInput.match(/(?:^|[^\d])(\d{1,2})\s*월/);
    if (mm) {
      const mo = parseInt(mm[1], 10);
      if (mo >= 1 && mo <= 12) {
        const start = new Date(y, mo - 1, 1);
        const end = new Date(y, mo, 0);
        return { startDate: fmt(start), endDate: fmt(end) };
      }
    }
  }

  return null;
}

// YYYY-MM 월 집합 추출 (월 단위 비교 — day 경계 차이 무시) //
function _monthSet(startDate, endDate, capMonths = 36) {
  const set = new Set();
  if (!startDate || !endDate) return set;
  let sy = parseInt(String(startDate).slice(0, 4), 10);
  let sm = parseInt(String(startDate).slice(5, 7), 10);
  const ey = parseInt(String(endDate).slice(0, 4), 10);
  const em = parseInt(String(endDate).slice(5, 7), 10);
  if (!sy || !sm || !ey || !em) return set;
  let count = 0;
  while ((sy < ey || (sy === ey && sm <= em)) && count < capMonths) {
    set.add(`${sy}-${String(sm).padStart(2, '0')}`);
    sm++; if (sm > 12) { sm = 1; sy++; }
    count++;
  }
  return set;
}

function _monthSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const m of a) if (!b.has(m)) return false;
  return true;
}

// "만" narrow particle (기존 NARROW_PERIOD 가드에 위임 — 본 detector 미개입) //
const _NARROW_PARTICLE_RE = /(오늘|어제|이번\s*주|이번\s*달|이달|금주|금월|지난\s*주|지난\s*달|\d+\s*월|\d+\s*주|\d+\s*일|최근\s*\d+\s*일)\s*만/;

/**
 * 상대기간 후속 턴 stale 버퍼 거짓 라벨 감지.
 * @param {{userInput:string, buffer:object, calledApiThisTurn:boolean, today?:Date}} arg
 * @returns {{block:true, requestedPeriod:{startDate,endDate}, bufferPeriod:{startDate,endDate}, intention:?string}|null}
 */
export function detectStalePeriodRelabel({ userInput, buffer, calledApiThisTurn, today = new Date() } = {}) {
  if (calledApiThisTurn) return null;                    // (a) 본 turn 재조회함 → 정상 흐름
  const items = buffer?.items;
  const dr = buffer?.dateRange;
  if (!Array.isArray(items) || items.length < 1) return null;   // (b1) 표시할 버퍼 없음
  if (!dr?.startDate || !dr?.endDate) return null;              // (b2) period 버퍼 아님(유효회원 LIST 등 제외)
  const input = userInput || '';
  if (_NARROW_PARTICLE_RE.test(input)) return null;     // "만" narrow → 기존 가드 위임(family 분리)
  if (!ABS_PERIOD_KW_RE.test(input)) return null;        // (d) 절대기간 키워드 부재
  const req = resolveRequestedAbsolutePeriod(input, today);
  if (!req) return null;                                 // resolve 모호 → 미개입(보수적)
  const reqMonths = _monthSet(req.startDate, req.endDate);
  const bufMonths = _monthSet(dr.startDate, dr.endDate);
  if (reqMonths.size === 0 || bufMonths.size === 0) return null;
  if (_monthSetsEqual(reqMonths, bufMonths)) return null;  // (e) 같은 달력 기간 → 재라벨 아님
  return {
    block: true,
    requestedPeriod: req,
    bufferPeriod: { startDate: dr.startDate, endDate: dr.endDate },
    intention: buffer?.intention || null,
  };
}
